import {
  FIELD_SUBTEMPLATE_KEY,
  FORM_ROLES_KEY,
  FORM_SUBTEMPLATES_KEY,
  NO_SUBTEMPLATE_VALUE
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useMultiActivityFormStoreActions } from "PFApp/stores/activity_form/multi_roles_form";
import { useManageRolesStore, useManageRolesStoreActions } from "PFApp/stores/manage_roles";
import { ManageRolesMultiForm } from "PFApp/types/activity_form/manage_roles_form_schema";
import { Subtemplate } from "PFCore/types";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";

import { DRAFT_CREATE_SENTINEL } from "../manage_roles_constants";
import { useUnsavedEditsSyncContext } from "../parts/unsaved_edits_sync_context";
import { useNewDraftRole } from "./use_new_draft_role";

export const useAddDraftRole = () => {
  const { getValues, reset } = useFormContext<ManageRolesMultiForm>();
  const { getNewDraftRole } = useNewDraftRole();
  const { addNewDraftRole, addUnsavedEdit } = useManageRolesStoreActions();
  const { setRoleData } = useMultiActivityFormStoreActions();
  const { trackerResetVersionRef } = useUnsavedEditsSyncContext();
  const currentPage = useManageRolesStore((state) => state.currentPage);

  const addDraftRole = useCallback(
    (subtemplate?: Subtemplate) => {
      const subtemplateKey = subtemplate?.subtemplate_key ?? NO_SUBTEMPLATE_VALUE;
      const draft = getNewDraftRole({ subtemplateKey });
      addNewDraftRole(draft);

      const currentValues = getValues();
      const subtemplates = currentValues[FORM_SUBTEMPLATES_KEY] ?? [];
      const subtemplateIndex = subtemplates.findIndex(
        (item) => item[FIELD_SUBTEMPLATE_KEY] === subtemplateKey
      );

      const nextSubtemplates =
        subtemplateIndex >= 0
          ? subtemplates.map((item, index) =>
              index === subtemplateIndex
                ? { ...item, [FORM_ROLES_KEY]: [draft, ...(item[FORM_ROLES_KEY] ?? [])] }
                : item
            )
          : [...subtemplates, { [FIELD_SUBTEMPLATE_KEY]: subtemplateKey, [FORM_ROLES_KEY]: [draft] }];

      trackerResetVersionRef.current += 1;
      reset(
        { ...currentValues, [FORM_SUBTEMPLATES_KEY]: nextSubtemplates },
        { keepErrors: true, keepTouched: true, keepIsSubmitted: true, keepSubmitCount: true }
      );

      setRoleData(draft.uniqueId, { activityCreator: null, attachments: [] });

      addUnsavedEdit(draft.uniqueId, DRAFT_CREATE_SENTINEL, null, undefined, currentPage);
    },
    [
      addNewDraftRole,
      addUnsavedEdit,
      currentPage,
      getNewDraftRole,
      getValues,
      reset,
      setRoleData,
      trackerResetVersionRef
    ]
  );

  return { addDraftRole };
};
